/**
 * Store one processed photo, retrying transient failures.
 *
 * The store endpoint (`POST /photography/:id/photos/process`) writes the blob to
 * R2 and updates D1. Its own errors return HTTP 500 with a JSON body; a bare
 * 502/503/504 — or a dropped connection — is the Cloudflare runtime, not the
 * handler, and one such blip showed up mid-batch on 2026-08-31 (Sentry,
 * photography 3015). Those clear on a retry, so a single hiccup should never
 * fail the photo — and, upstream, must never abandon the rest of the batch.
 *
 * Retries 5xx, 429 and network errors with linear backoff. A 4xx (bar 429) is
 * the caller's fault and will not get better, so it fails fast.
 *
 * `fetchImpl` and `sleep` are injectable for tests.
 *
 * @param {number|string} itemId - photography_queue_id
 * @param {number|string} photoId - photography_photo_id
 * @param {Blob} blob - the processed JPEG to store
 * @returns {Promise<void>} resolves once stored; rejects if every attempt failed
 */
export async function uploadProcessedPhoto(itemId, photoId, blob, opts = {}) {
  const {
    retries = 3,
    backoffMs = 500,
    fetchImpl = fetch,
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
  } = opts

  let lastError
  for (let attempt = 0; attempt <= retries; attempt++) {
    // Rebuild the body each attempt: a FormData sent through fetch is consumed
    // and cannot be replayed.
    const formData = new FormData()
    formData.append('photo', blob, 'processed.jpg')
    formData.append('photoId', String(photoId))

    let res
    try {
      res = await fetchImpl(`/api/warehouse/photography/${itemId}/photos/process`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
    } catch (err) {
      lastError = err // network-level failure — retryable
      if (attempt < retries) { await sleep(backoffMs * (attempt + 1)); continue }
      throw lastError
    }

    if (res.ok) return

    // 4xx (except 429 rate-limit) won't improve on retry — fail immediately.
    if (res.status < 500 && res.status !== 429) {
      throw new Error(`Upload failed: ${res.status}`)
    }

    lastError = new Error(`Upload failed: ${res.status}`) // 5xx / 429 — retryable
    if (attempt < retries) await sleep(backoffMs * (attempt + 1))
  }

  throw lastError || new Error('Upload failed')
}
